<template>
  <div class="group-form">
    <section class="group-card" data-cy="group-details">
      <!-- A saved group opens read-only: Edit Group unlocks it. Hidden, not removed, while editing, so nothing shifts. -->
      <div class="group-card__heading">
        <h3 class="group-card__title">Group Details</h3>
        <b-button
          v-if="group"
          variant="blue"
          :class="{ 'group-form__edit--hidden': editing }"
          :aria-hidden="editing ? 'true' : null"
          :tabindex="editing ? -1 : null"
          data-cy="group-edit"
          @click="editing = true"
        >
          Edit Group
        </b-button>
      </div>
      <p class="group-card__subtitle">Name this automation group internally.</p>

      <fieldset class="group-form__fields" :disabled="!editing">
        <b-form-group
          label="Group Name"
          label-for="group-name"
          label-class="group-card__label"
        >
          <b-form-input
            id="group-name"
            v-model="form.name"
            type="text"
            class="group-card__control group-card__control--name"
            data-cy="group-name"
          />
        </b-form-group>

        <!-- The Groups list's Customer filter checkbox, so the two read as one control. -->
        <label class="group-card__check">
          <input
            v-model="form.visible"
            type="checkbox"
            class="visibility-checkbox"
            data-cy="group-visible"
          />
          Show in the Customers filter
        </label>
        <p class="group-card__hint">
          Lists this group under “Start from a saved group” in the Customers
          filter.
        </p>
      </fieldset>
    </section>

    <section class="group-card" data-cy="group-filters">
      <h3 class="group-card__title">Filters</h3>
      <p class="group-card__subtitle">
        An account joins this group only when it matches every condition below.
        Conditions are re-evaluated on the schedule you set.
      </p>

      <fieldset class="group-form__fields" :disabled="!editing">
        <GroupFilterRowsEditor :key="formVersion" :rows="form.filters" />
      </fieldset>

      <p class="match-count" data-cy="match-count">
        <template v-if="matchCount === null"
          >Add a filter to see how many accounts match.</template
        >
        <template v-else-if="matchCount === 0"
          ><strong>0 accounts</strong> currently match these filters.</template
        >
        <template v-else>
          <button
            type="button"
            class="match-count__preview"
            title="Preview the matching accounts"
            data-cy="match-count-preview"
            @click="$bvModal.show('group-preview-modal')"
          >
            {{ matchCount.toLocaleString() }}
            {{ matchCount === 1 ? 'account' : 'accounts' }}
          </button>
          currently {{ matchCount === 1 ? 'matches' : 'match' }} these filters.
        </template>
      </p>

      <GroupPreviewModal :filter="filterJson" />
    </section>

    <section class="group-card group-card--split" data-cy="group-automation">
      <div class="group-card__half">
        <h3 class="group-card__title">Assign Templates</h3>
        <p class="group-card__subtitle">
          The message sent to accounts in this group.
        </p>

        <fieldset class="group-form__fields" :disabled="!editing">
          <b-form-group label="Template" label-class="group-card__label">
            <TemplatePicker
              id="group-template"
              :templates="templateChoices"
              :label="templateLabel"
              variant="light"
              block
              class="group-card__control group-picker group-picker--template"
              @select="form.templateId = $event"
            >
              <template #button-content>
                <span class="group-picker__text">{{ templateLabel }}</span>
              </template>
            </TemplatePicker>
          </b-form-group>
        </fieldset>

        <!-- Read-only: the template's text is edited on the Templates page, never here. -->
        <div
          v-if="selectedTemplateText !== null"
          class="template-preview"
          data-cy="group-template-preview"
        >
          <span class="group-card__label">Message</span>
          <!-- v-text, not {{ }} on its own line: pre-wrap renders the template's indent as a leading space. -->
          <p
            class="template-preview__text"
            v-text="
              selectedTemplateText || 'This template has no text message.'
            "
          />
          <span class="template-preview__note">
            Edit this text on the
            <!-- A new tab, so the group being edited here is not lost. -->
            <router-link
              v-if="selectedTemplateRoute"
              :to="selectedTemplateRoute"
              target="_blank"
              data-cy="group-template-link"
              >Templates page</router-link
            ><template v-else>Templates page</template>.
          </span>
        </div>
      </div>

      <div class="group-card__half">
        <div class="group-card__heading">
          <h3 class="group-card__title">Automation Schedule</h3>
          <fieldset class="group-form__fields" :disabled="!editing">
            <b-form-checkbox
              v-if="automated"
              v-model="form.active"
              switch
              class="group-active-switch"
              :disabled="activeLocked"
              :title="
                activeLocked
                  ? 'This schedule has ended. Change the schedule to restart it.'
                  : ''
              "
              data-cy="group-active"
            >
              {{ activeLocked ? 'Ended' : form.active ? 'Active' : 'Paused' }}
            </b-form-checkbox>
          </fieldset>
        </div>
        <p class="group-card__subtitle">When matched accounts are messaged.</p>

        <p
          v-if="!automated"
          class="group-card__subtitle"
          data-cy="group-no-automation"
        >
          No automation: this group is a saved filter and sends nothing. Choose
          a template to schedule messages.
        </p>
        <p
          v-else-if="!showSchedule"
          class="group-card__subtitle"
          data-cy="group-paused"
        >
          Paused: nothing is sent. Switch it on to set the schedule.
        </p>
        <fieldset v-else class="group-form__fields" :disabled="!editing">
          <GroupScheduleEditor
            :days.sync="form.days"
            :schedule.sync="form.schedule"
            :send-time="form.sendTime"
            :saved="group && group.automation"
          >
            <template #send-time>
              <b-form-group
                label="Send Time"
                label-for="group-send-time"
                label-class="group-card__label"
              >
                <b-form-select
                  id="group-send-time"
                  v-model="form.sendTime"
                  :options="sendTimeChoices"
                  class="group-card__control group-card__control--time"
                  data-cy="group-send-time"
                />
              </b-form-group>
            </template>
          </GroupScheduleEditor>
        </fieldset>
      </div>
    </section>

    <!-- Only while editing: a read-only group has nothing to save, cancel or delete until Edit Group. -->
    <footer
      v-if="editing"
      class="group-card group-footer"
      data-cy="group-footer"
    >
      <span class="group-footer__note">
        By saving, you confirm you have consent to message the accounts this
        group matches.
      </span>
      <div class="group-footer__actions">
        <b-button
          v-if="group && group.id"
          variant="outline-danger"
          data-cy="group-delete"
          :disabled="saving"
          @click="confirmDelete"
        >
          Delete Group
        </b-button>
        <b-button
          variant="outline-secondary"
          data-cy="group-cancel"
          :disabled="saving"
          @click="cancelEdit"
        >
          Cancel
        </b-button>
        <b-button
          variant="blue"
          data-cy="group-save"
          :disabled="saving"
          @click="save"
        >
          Save Group
        </b-button>
      </div>
    </footer>
  </div>
</template>

<script lang="ts">
import { toErrorList } from '@/utils/apiErrors'
import endpoints from '@/utils/endpoints.js'
import { cfAxios } from '@/utils/init-axios.js'
import {
  filterFromRows,
  rowsFromFilter,
  rowsProblem
} from '@/views/MessageCenter/groupFilterRows.js'
import TemplatePicker from '@/components/MessageTemplate/TemplatePicker/TemplatePicker.vue'
import GroupFilterRowsEditor from './GroupFilterRowsEditor.vue'
import GroupPreviewModal from './GroupPreviewModal.vue'
import GroupScheduleEditor from './GroupScheduleEditor.vue'
import {
  Schedule,
  hasEnded,
  isScheduleChanged,
  normalizeSchedule,
  scheduleBody,
  scheduleProblem,
  timeLabel
} from './schedule'
import { FilterRow } from './types'
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { mapGetters } from 'vuex'

/** The group row handed over from the list; null when creating. */
interface GroupSeed {
  id?: string
  pkid?: number
  name?: string
  filter?: Record<string, unknown>
  /** Listed under "Start from a saved group" in the Customers filter. */
  visible_group?: boolean
  automation?: {
    template_id: number | null
    is_active: boolean
    days_of_week: number[]
    send_time: string
    schedule?: Schedule | null
    sends_count?: number
  } | null
}

interface TemplateBody {
  language: string
  body_type: string
  message_body: string
}

// The body the template editor shows and saves: English, text message.
function englishTextBody(bodies: TemplateBody[] = []): string {
  return (
    bodies.find(body => body.language === 'en-US' && body.body_type === 'TEXT')
      ?.message_body ?? ''
  )
}

/** A pkid for core. A numeric string still counts; a UUID does not. */
function positivePkid(value: unknown): number | null {
  const pkid = typeof value === 'number' ? value : Number(value)
  if (!Number.isSafeInteger(pkid) || pkid <= 0) return null

  return pkid
}

/** 8:00 AM to 7:45 PM in 15-minute steps. */
const SEND_TIME_OPTIONS = Array.from({ length: 48 }, (_, step) => {
  const minutes = 8 * 60 + step * 15
  const value = `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`

  return { value, text: timeLabel(value) }
})

/** The Template choice for a group that sends nothing; 0 is never a template pkid. */
const NO_AUTOMATION = 0
const NO_AUTOMATION_TEXT = 'None (no automation)'

@Component({
  components: {
    GroupFilterRowsEditor,
    GroupPreviewModal,
    GroupScheduleEditor,
    TemplatePicker
  },
  computed: { ...mapGetters(['currentMerchant']) }
})
export default class GroupForm extends Vue {
  @Prop({ type: Object, default: null }) group!: GroupSeed | null
  /** Pre-selects a template pkid when opened from a template's "+ Add Automated Text". */
  @Prop({ type: Number, default: null }) templateId!: number | null

  currentMerchant!: { id: string; pkid: number }

  saving = false
  private countRequest = 0
  templateOptions: Array<{ value: number; text: string; body: string }> = []
  /** Each template's id by pkid: the Templates page addresses a template by id. */
  templateIds: Record<number, string> = {}
  matchCount: number | null = null
  private countTimer: ReturnType<typeof setTimeout> | null = null

  /** A saved group opens read-only until Edit Group; a new one opens editable. */
  editing = !this.group
  /** Bumped when Cancel restores the form, so the filter rows and their pickers rebuild from it. */
  formVersion = 0
  form = this.openingForm()

  // What core holds; Save Group sends only what differs from it.
  savedVisible = this.group?.visible_group ?? true
  savedAutomation = this.group?.automation ? { ...this.group.automation } : null
  /** editSignature as the editor opened; set in created, once the form and its filter exist. */
  openedSignature = ''

  /** The saved-filter JSON: core-service's group filter keys, the same the Customers filter writes. */
  get filterJson(): Record<string, unknown> {
    return filterFromRows(this.form.filters)
  }

  /** The 15-minute grid, plus the saved time when it is off-grid (converted reminders keep 08:05). */
  get sendTimeChoices() {
    const time = this.form.sendTime
    if (!time || SEND_TIME_OPTIONS.some(option => option.value === time)) {
      return SEND_TIME_OPTIONS
    }

    return [{ value: time, text: timeLabel(time) }, ...SEND_TIME_OPTIONS].sort(
      (x, y) => x.value.localeCompare(y.value)
    )
  }

  /** The chosen template's English text; null until a template is chosen and loaded. */
  get selectedTemplateText(): string | null {
    const option = this.templateOptions.find(
      template => template.value === this.form.templateId
    )

    return option ? option.body : null
  }

  /** The first thing wrong with the form, or null when it can be saved. */
  get validationError(): string | null {
    if (!this.form.name.trim()) return 'Give the group a name.'
    const rowProblem = rowsProblem(this.form.filters)
    if (rowProblem) return rowProblem
    if (!Object.keys(this.filterJson).length) return 'Add a filter.'
    if (!this.automated) return null
    if (!positivePkid(this.form.templateId)) return 'Choose a template.'
    if (this.form.schedule) {
      const problem = scheduleProblem(this.form.schedule)
      if (problem) return problem
    } else if (!this.form.days.length) {
      // core-service needs the days even while paused, and pausing hides the day picker.
      return this.showSchedule
        ? 'Pick at least one day.'
        : 'Switch it on to pick the days it sends, then pause it.'
    }

    return null
  }

  created() {
    this.openedSignature = this.editSignature
  }

  mounted() {
    this.refreshMatchCount()
    this.loadTemplates()
    window.addEventListener('beforeunload', this.warnBeforeUnload)
  }

  beforeDestroy() {
    window.removeEventListener('beforeunload', this.warnBeforeUnload)
  }

  // A refresh or a closed tab cannot show the app's modal; the browser asks instead.
  warnBeforeUnload(event: BeforeUnloadEvent) {
    if (!this.isDirty) return
    event.preventDefault()
    event.returnValue = ''
  }

  /** The Templates page's editor for the chosen template. */
  get selectedTemplateRoute(): object | null {
    const id = this.form.templateId && this.templateIds[this.form.templateId]
    if (!id) return null

    return {
      name: 'MessageTemplateEdit',
      params: {
        merchantID: this.currentMerchant.id,
        messageTemplateID: id
      }
    }
  }

  /** Whether the group sends messages: "None (no automation)" is the one choice that does not. */
  get automated(): boolean {
    return this.form.templateId !== NO_AUTOMATION
  }

  /** "None (no automation)" first, then the templates A-Z. */
  get templateChoices() {
    return [
      { value: NO_AUTOMATION, text: NO_AUTOMATION_TEXT, body: '' },
      ...[...this.templateOptions].sort((a, b) => a.text.localeCompare(b.text))
    ]
  }

  get templateLabel(): string {
    if (!this.automated) return NO_AUTOMATION_TEXT

    return this.selectedTemplateName || 'Select a template'
  }

  get selectedTemplateName(): string | null {
    return (
      this.templateOptions.find(
        template => template.value === this.form.templateId
      )?.text ?? null
    )
  }

  /** An ended schedule that was edited starts again, since the edit resets its send count. */
  get restartsOnSave(): boolean {
    const saved = this.group?.automation ?? null

    return (
      hasEnded(saved) && isScheduleChanged(saved?.schedule, this.form.schedule)
    )
  }
  /** A saved schedule that has ended stays off until the schedule changes; core refuses to switch it on. */
  get activeLocked(): boolean {
    const saved = this.savedAutomation

    // An ending schedule stays on through its last send day, and can be paused then.
    return hasEnded(saved) && !saved?.is_active && !this.restartsOnSave
  }

  // Changing an ended schedule restarts it, as saving one always has.
  @Watch('restartsOnSave')
  onRestart(restarts: boolean) {
    if (restarts) this.form.active = true
  }

  /** What the automation PUT carries for the schedule: Custom wins over the simple day picker. */
  scheduleFields() {
    const schedule = this.form.schedule
    if (!schedule) {
      return { days_of_week: [...this.form.days].sort(), schedule: null }
    }
    const body = scheduleBody(schedule)

    return { days_of_week: body.days_of_week, schedule: body }
  }

  async loadTemplates() {
    try {
      const response = await this.axios.get(
        endpoints.listMessageTemplates(this.currentMerchant.id),
        { params: { page: 1, page_size: 1000 } }
      )
      const results: Array<{
        pkid: number
        id: string
        name: string
        message_bodies?: TemplateBody[]
      }> = response.data?.results ?? []
      this.templateOptions = results.map(template => ({
        value: template.pkid,
        text: template.name,
        body: englishTextBody(template.message_bodies)
      }))
      this.templateIds = Object.fromEntries(
        results.map(template => [template.pkid, template.id])
      )
    } catch {
      this.templateOptions = []
    }
  }

  // The accounts this group's automation would message, asked for one row.
  @Watch('filterJson', { deep: true })
  refreshMatchCount() {
    if (this.countTimer) clearTimeout(this.countTimer)
    const params = this.filterJson
    if (!Object.keys(params).length) {
      this.matchCount = null

      return
    }
    this.countTimer = setTimeout(async () => {
      // A slower earlier answer must not overwrite the count for newer filters.
      const request = ++this.countRequest
      try {
        const response = await cfAxios.post(
          endpoints.customerGroupsPreview(this.currentMerchant.pkid),
          { filter: params, page: 1, page_size: 1 }
        )
        if (request === this.countRequest) {
          this.matchCount = response.data?.count ?? null
        }
      } catch {
        if (request === this.countRequest) this.matchCount = null
      }
    }, 400)
  }

  async confirmDelete() {
    const group = this.group
    const groupPkid = positivePkid(group?.pkid)
    if (!group || groupPkid === null) return
    const ok = await this.$bvModal.msgBoxConfirm(
      `Delete "${group.name}"? Its automation stops with it.`,
      { title: 'Delete group', okVariant: 'danger', okTitle: 'Delete' }
    )
    if (!ok) return
    this.saving = true
    try {
      await cfAxios.delete(
        endpoints.customerGroup(this.currentMerchant.pkid, groupPkid)
      )
      this.$emit('deleted')
    } catch {
      this.$bvToast.toast('Failed to delete the group', {
        title: 'Groups',
        variant: 'danger'
      })
    } finally {
      this.saving = false
    }
  }

  /** Core keys the group and the automation by pkid, and takes template_id as a number. */
  /** What Save Group would write, in saved shape: rows normalise to the filter, so a picker's own echo is not an edit. */
  get editSignature(): string {
    const { name, templateId, days, schedule, sendTime, visible, active } =
      this.form

    return JSON.stringify({
      name: name.trim(),
      filter: this.filterJson,
      templateId,
      days: [...days].sort(),
      schedule,
      sendTime,
      visible,
      active
    })
  }

  /** Read by GroupsView before it leaves the editor. */
  get isDirty(): boolean {
    return this.editSignature !== this.openedSignature
  }

  /** The schedule shows while the group sends, and while an ended one waits to be changed and restarted. */
  get showSchedule(): boolean {
    return this.form.active || this.activeLocked
  }

  /** The form as the group was saved; Cancel restores it. */
  openingForm() {
    return {
      name: this.group?.name ?? '',
      filters: rowsFromFilter(this.group?.filter) as FilterRow[],
      // An existing group with no automation is a saved filter; a new one starts with nothing chosen.
      templateId: this.group?.automation
        ? (this.group.automation.template_id ?? null)
        : this.group
          ? NO_AUTOMATION
          : (this.templateId ?? null),
      days: [...(this.group?.automation?.days_of_week ?? [])],
      schedule: normalizeSchedule(this.group?.automation?.schedule),
      sendTime: this.group?.automation?.send_time ?? '08:00',
      visible: this.group?.visible_group ?? true,
      active: this.group?.automation?.is_active ?? true
    }
  }

  /** Cancel: a new group leaves the editor; a saved one drops its edits, asking first, and locks again. */
  async cancelEdit() {
    if (!this.group) {
      this.$emit('cancel')

      return
    }
    if (this.isDirty && !(await this.confirmDiscard())) return
    this.form = this.openingForm()
    this.formVersion += 1
    this.editing = false
  }

  confirmDiscard(): Promise<boolean> {
    return this.$bvModal.msgBoxConfirm(
      'Your changes to this group will be lost.',
      {
        title: 'Discard changes?',
        okVariant: 'danger',
        okTitle: 'Discard',
        cancelTitle: 'Keep editing'
      }
    )
  }

  confirmRemoveAutomation(): Promise<boolean> {
    return this.$bvModal.msgBoxConfirm(
      "Remove this group's automation? It stops sending messages. The group and its filters stay.",
      {
        title: 'Remove automation',
        okVariant: 'danger',
        okTitle: 'Remove automation'
      }
    )
  }

  async save() {
    const problem = this.validationError
    if (problem) {
      this.$bvToast.toast(problem, { title: 'Groups', variant: 'warning' })

      return
    }
    const templatePkid = positivePkid(this.form.templateId)
    if (this.automated && templatePkid === null) return
    const removing = !this.automated && Boolean(this.savedAutomation)
    if (removing && !(await this.confirmRemoveAutomation())) return
    this.saving = true
    try {
      const body = { name: this.form.name.trim(), filter: this.filterJson }
      let groupPkid = positivePkid(this.group?.pkid)
      if (this.group && groupPkid === null) {
        this.$bvToast.toast(
          'This group has no pkid. Reload the page and try again.',
          {
            title: 'Groups',
            variant: 'danger'
          }
        )

        return
      }
      if (groupPkid !== null) {
        await cfAxios.put(
          endpoints.customerGroup(this.currentMerchant.pkid, groupPkid),
          body
        )
      } else {
        groupPkid = positivePkid(
          (
            await cfAxios.post(
              endpoints.customerGroups(this.currentMerchant.pkid),
              body
            )
          ).data?.group?.pkid
        )
        if (groupPkid === null) {
          this.$bvToast.toast(
            'The group was saved, but its automation was not. Reload the page and edit the group.',
            { title: 'Groups', variant: 'danger' }
          )

          return
        }
      }
      // A new group is created shown, so only an unsaved change (or a new hidden group) is sent.
      if (this.form.visible !== this.savedVisible) {
        await cfAxios.put(
          endpoints.customerGroupVisibility(
            this.currentMerchant.pkid,
            groupPkid
          ),
          { visible_group: this.form.visible }
        )
      }
      if (!this.automated) {
        if (removing) {
          await cfAxios.delete(
            endpoints.groupAutomation(this.currentMerchant.pkid, groupPkid)
          )
        }
        this.$emit('saved')

        return
      }
      await cfAxios.put(
        endpoints.groupAutomation(this.currentMerchant.pkid, groupPkid),
        {
          template_id: templatePkid,
          is_active: this.form.active && !this.activeLocked,
          ...this.scheduleFields(),
          send_time: this.form.sendTime
        }
      )
      this.$emit('saved')
    } catch (error) {
      const [message] = toErrorList(
        (error as { response?: { data?: unknown } })?.response?.data,
        'Failed to save the group'
      )
      this.$bvToast.toast(message, { title: 'Groups', variant: 'danger' })
    } finally {
      this.saving = false
    }
  }
}
</script>

<style lang="scss" scoped>
// Bootstrap colours a switch $primary; the page's buttons and the list's Active pill are $button-blue.
// Its read-only (disabled) rule ties on specificity and loads later, so it is restated here.
.group-active-switch
  ::v-deep
  .custom-control-input:checked
  ~ .custom-control-label::before {
  background-color: $button-blue;
  border-color: $button-blue;
}

.group-active-switch
  ::v-deep
  .custom-control-input:disabled:checked
  ~ .custom-control-label::before {
  background-color: transparentize($button-blue, 0.5);
  border-color: transparent;
}

$ink: #1f2937;
$line: #c7ced8;

// Same left edge as the toolbar above (.button-toolbar, scss/main.scss).
.group-form {
  margin: 25px;
}

// Keeps its space while editing, so the card does not move when it is clicked.
.group-form__edit--hidden {
  visibility: hidden;
}

// A bare wrapper: no fieldset border or spacing, only the disabling.
.group-form__fields {
  min-width: 0;
  margin: 0;
  padding: 0;

  border: 0;

  // The tag pickers' remove crosses are not form controls, so a disabled fieldset leaves them clickable.
  &:disabled ::v-deep .ti-actions {
    display: none;
  }
}

.group-card {
  padding: 32px 36px 24px;

  background: #fff;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

  & + & {
    margin-top: 24px;
  }

  &--split {
    display: flex;
    gap: 48px;
  }
}

.group-card__half {
  flex: 1;
  min-width: 0;
}

.template-preview {
  max-width: 420px;

  .group-card__label {
    display: block;
  }
}

.template-preview__text {
  margin: 0 0 6px;
  padding: 12px 14px;
  color: $ink;

  font-size: 14px;
  white-space: pre-wrap;

  background: $background;
  border: 1px solid $line;
  border-radius: 6px;
}

.template-preview__note {
  color: $darker-gray;

  font-size: 12px;
}

.group-card__title {
  margin: 0;

  font-size: 22px;
  font-weight: 700;
}

.group-card__heading {
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
}

.group-card__check {
  display: flex;
  gap: 8px;
  align-items: center;
  margin: 8px 0 4px;

  font-weight: 600;
  cursor: pointer;
}

// The Groups list's Customer filter checkbox.
.visibility-checkbox {
  width: 16px;
  height: 16px;
  margin: 0;

  accent-color: $button-blue;
}

.group-card__hint {
  margin-bottom: 0;

  color: $darker-gray;
  font-size: 13px;
}

.group-card__subtitle {
  margin: 7px 0 25px;
  color: $darker-gray;

  font-size: 14.5px;
}

.group-card__control {
  max-width: 420px;
  height: 47px;

  font-size: 15.5px;

  &--name {
    max-width: 560px;
  }

  &--time {
    max-width: 240px;
  }
}

.group-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
}

.group-footer__note {
  color: $darker-gray;

  font-size: 12px;
}

.group-footer__actions {
  display: flex;
  gap: 10px;
}

.match-count {
  margin: 20px 0 0;
  padding-top: 16px;
  color: $darker-gray;

  font-size: 14px;

  border-top: 1px solid #e5e8ee;

  strong {
    color: $ink;
  }
}

// The count opens the preview, as the story asks: it reads as a link, in the app's link blue.
.match-count__preview {
  padding: 0;
  color: $button-blue;

  font-weight: 700;
  text-decoration: underline;

  background: transparent;
  border: 0;
  cursor: pointer;

  &:hover {
    color: $hover-button-blue;
  }
}
</style>

<style lang="scss">
// TemplatePicker (the chat's searchable template list) dressed as a form field showing its choice.
// Not scoped: GroupFilterRowsEditor's filter pickers use it too.
.group-picker .dropdown-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  overflow: hidden;
  color: #1f2937;

  text-align: left;
  white-space: nowrap;
  text-overflow: ellipsis;

  background: #fff;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  box-shadow: none;

  &:hover,
  &:focus,
  &:active {
    color: #1f2937;

    background: #fff;
    border-color: $button-blue;
  }
}

// The label truncates and the caret never shrinks, so the arrow stays visible however narrow the picker gets.
.group-picker__text {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;

  text-overflow: ellipsis;
}

.group-picker .dropdown-toggle::after {
  flex-shrink: 0;
}

.group-picker--template .dropdown-toggle {
  height: 47px;

  font-size: 15.5px;
}

// Not scoped: b-form-group renders the label outside this component's scope id.
.group-card__label {
  margin-bottom: 9px;
  color: #1f2937;

  font-size: 12.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
</style>
